import { getVocabulary } from 'components/Vocabulary';
import {
  cloneDeep,
  compact,
  endsWith,
  filter,
  forEach,
  includes,
  isEmpty,
  isUndefined,
  map,
  split,
} from 'lodash';

const SEPARATOR_EXPRESSION = '/';

const getPhantomNodeSuffix = () => {
  return `${getVocabulary('product', true)}/${getVocabulary('subProduct', true)}`;
};

export type Node = {
  title: string;
  key: string;
  parent?: Node;
  children: Node[];
  _children: Node[];
  expanded?: boolean;
  selected?: boolean;
  _groupValue: string[];
  groupValue: string;
  phantomNode?: boolean;
};

export function parseGroupsToHierarchy(
  groups?: string[],
  preventRegXOnKey = false,
  showPhantomNode = false,
) {
  if (!groups) {
    return {
      allNodes: [],
      hierarchy: [],
    };
  }
  const hierarchy: Node[] = [];
  const allNodes: Node[] = [];
  for (let i = 0; i < groups.length; ++i) {
    const path = groups[i];
    if (path) {
      let level = hierarchy;
      const parts = compact(path.split(SEPARATOR_EXPRESSION));
      for (let j = 0; j < parts.length; ++j) {
        const part = parts[j];
        if (part) {
          const _node = level.find((n) => n.title === part);
          if (_node) {
            level = _node.children;
            _node._groupValue.push(path);
          } else {
            let key = parts.slice(0, j + 1).join('/');
            if (path.startsWith('/')) {
              key = `/${key}`;
            }
            const __node: Node = {
              title: part,
              key,
              children: [],
              _groupValue: [path],
              groupValue: '',
              expanded: false,
              _children: [],
            };
            allNodes.push(__node);
            level.push(__node);
            level = __node.children;
            if (
              showPhantomNode &&
              allNodes.find((node) => node.key === path) &&
              !level.find((node) => node.phantomNode)
            ) {
              const phantomNode: Node = {
                title: `[${part} ${getPhantomNodeSuffix()}]`,
                key: renderGroups(key),
                children: [],
                _groupValue: [renderGroups(path)],
                groupValue: '',
                expanded: false,
                _children: [],
                phantomNode: true,
              };
              allNodes.push(phantomNode);
              level.unshift(phantomNode);
            }
          }
        }
      }
    }
  }
  allNodes.forEach((node) => {
    // updqating the key with regex to include all under this group, doesn't matter if node has children or not
    if (
      !node.phantomNode &&
      !preventRegXOnKey &&
      node.key !== ARMORCODE_GROUP_NONE_VALUE
    ) {
      node.key = `${node.key}/.*`;
    }
    if (node.children.length === 1 && node.children[0].phantomNode) {
      node.children = [];
    }
    node.groupValue = node._groupValue.join(',');
    node._children = node.children;
    node.children.forEach((child) => (child.parent = node));
  });
  return { hierarchy, allNodes };
}

export const ARMORCODE_GROUP_KEY = 'armorcode.group';
export const ARMORCODE_TAG_KEY_PREFIX = 'armorcode.';
export const ARMORCODE_GROUP_NONE_VALUE = '<None>';

export const renderGroups = (group: string) => {
  // Do not change this logic, the name is render, but is used in calculating data as well
  return endsWith(group, '/.*') ? group.substring(0, group.length - 3) : group;
};

export const hasChildren = (allGroups: string[], matchGroup: string) => {
  matchGroup = renderGroups(matchGroup);
  return !!allGroups.find((group) => {
    return group.startsWith(`${matchGroup}/`);
  });
};

export const filterGroupsByGroupsFilter = (
  allGroups: string[],
  filteredGroups: string[],
) => {
  if (!isEmpty(filteredGroups)) {
    return allGroups.filter((group) => {
      // selected group ending with /.* should include itself and children
      // group not ending with /.* should only include itself
      return filteredGroups.some((globalFilteredGroup) => {
        if (globalFilteredGroup.endsWith('/.*')) {
          const _globalFilteredGroup = renderGroups(globalFilteredGroup);
          return (
            group.startsWith(_globalFilteredGroup + '/') || // to include children when /.*
            group === _globalFilteredGroup || // to include self when /.*
            group === globalFilteredGroup // to include children when not /.*
          );
        }
        return group === globalFilteredGroup;
      });
    });
  }
  return allGroups;
};

export const getFallingUnderGroup = (
  parentGroup: string,
  childrenGroup: string[],
) => {
  return childrenGroup
    ? childrenGroup.filter((childGroup) =>
        isFallingUnderGroup(parentGroup, childGroup),
      )
    : [];
};

export const isFallingUnderGroup = (
  parentGroup: string,
  childGroup: string,
) => {
  if (childGroup.startsWith(`${ARMORCODE_GROUP_KEY}:`)) {
    parentGroup = `${ARMORCODE_GROUP_KEY}:${parentGroup}`;
  }
  // checking ends with /.* as group filters at other places use it to select all
  // also existing widgets will have /.* in its data
  // with new hierarchy filter, user can select only parent, so with below regex logic it picks the child as well
  if (!parentGroup.endsWith('/.*')) {
    return parentGroup === childGroup;
  }
  return (
    childGroup.startsWith(`${renderGroups(parentGroup)}/`) ||
    renderGroups(parentGroup) === childGroup
  );
};

export const filterHierarchyDataFromFindingStats = (
  stats: Record<string, { severity: Record<string, number> }>,
  selectedGroups: string[],
  _config: { tagKey1: string; tagKey2: string },
): Record<string, { severity: Record<string, number> }> => {
  // this function filters the hierarchy which are selected in widget/dashboard/global filters
  if (
    !isEmpty(selectedGroups) &&
    [_config.tagKey1, _config.tagKey2].includes(ARMORCODE_GROUP_KEY)
  ) {
    if (_config.tagKey1 === ARMORCODE_GROUP_KEY) {
      const _stats = {};
      const groupKeys = Object.keys(stats);
      forEach(selectedGroups, (filteredGroupKey) => {
        forEach(
          getFallingUnderGroup(filteredGroupKey, groupKeys),
          (groupKey) => {
            // @ts-expect-error - Object indexing
            _stats[groupKey] ??= stats[groupKey];
          },
        );
      });
      return _stats;
    } else if (_config.tagKey2 === ARMORCODE_GROUP_KEY) {
      const _stats = cloneDeep(stats);
      forEach(_stats, (stat) => {
        const severity = {};
        const groupKeys = Object.keys(stat.severity);
        forEach(selectedGroups, (filteredGroupKey) => {
          forEach(
            getFallingUnderGroup(filteredGroupKey, groupKeys),
            (groupKey) => {
              // @ts-expect-error - Object indexing
              severity[groupKey] ??= stat.severity[groupKey];
            },
          );
        });
        stat.severity = severity;
      });
      return _stats;
    }
  }

  return stats;
};

export const getHierarchyName = (tag: string) => {
  return split(tag, `${ARMORCODE_GROUP_KEY}:`)[1];
};

export const ALL_HIERARCHY_KEY = 'ALL_HIERARCHY',
  NONE_HIERARCHY_KEY = '<None>';

export const getAllHierarchyNode = (): Node => ({
  title: 'All',
  key: ALL_HIERARCHY_KEY,
  children: [],
  _children: [],
  _groupValue: [],
  groupValue: '',
});

export const getNoneHierarchyNode = (): Node => ({
  title: `[No ${getVocabulary('group')}]`,
  key: NONE_HIERARCHY_KEY,
  children: [],
  _children: [],
  _groupValue: [],
  groupValue: '',
});

export const disableActionsOnAllOrNoneHierarchy = (group: string | undefined) =>
  isUndefined(group) ||
  [NONE_HIERARCHY_KEY, ALL_HIERARCHY_KEY].indexOf(group) !== -1;
export const getAllDescendants = (allGroupsString: string[], group: string) => {
  return filter(allGroupsString, (sourceGroup) => {
    const _group = renderGroups(group);
    return `${sourceGroup}/`.startsWith(`${_group}/`);
  });
};

export const getGroupExactName = (group: string) => {
  return renderGroups(group)?.split('/').pop() ?? '';
};

export const getNewGroupNamesForMovingGroups = (
  allGroupsString: string[],
  source: string,
  target?: string,
) => {
  const sourceGroupName = renderGroups(source);
  const sourceParentName = getImmediateParentGroupName(sourceGroupName);
  const targetName = target ? renderGroups(target) : undefined;
  const targetGroupName = compact([
    targetName,
    sourceParentName
      ? sourceGroupName.substring(sourceParentName.length + 1)
      : sourceGroupName,
  ]).join('/');
  return { sourceGroupName, targetGroupName };
};

export const getImmediateParentGroupName = (group: string) => {
  const allGroupComponents = renderGroups(group).split('/');
  const sourceParentName = allGroupComponents
    .slice(0, allGroupComponents.length - 1)
    .join('/');

  return sourceParentName;
};

export const isImmediateParentGroup = (parentGroup: string, group: string) => {
  return (
    renderGroups(getImmediateParentGroupName(group)) ===
    renderGroups(parentGroup)
  );
};

export const isAllOrNoneHierarchy = (group: string) =>
  isUndefined(group) ||
  isEmpty(group) ||
  [NONE_HIERARCHY_KEY, ALL_HIERARCHY_KEY].indexOf(group) !== -1;

export const extractHierarchyFromTags = (sourceTags?: string[]) =>
  map(sourceTags, (tag) => {
    const tagSource = tag.split(':');
    const [key] = tagSource;

    if (key === ARMORCODE_GROUP_KEY) {
      return tagSource.slice(1).join(':');
    }
    return null;
  });

export const extractEditableHierarchyFromTags = (
  sourceTags: string[],
  staticTags?: string[],
) =>
  map(sourceTags, (tag) => {
    const tagSource = tag.split(':');
    const [key] = tagSource;

    if (key === ARMORCODE_GROUP_KEY) {
      return {
        group: tagSource.slice(1).join(':'),
        disabled: includes(staticTags, tag),
      };
    }
    return null;
  }).filter(Boolean);

export const getIsPhantomGroup = (group: string) => {
  return !isAllOrNoneHierarchy(group) && !endsWith(group, '/.*');
};

export const getPhantomGroupTile = (group: string) => {
  return `[${group.split('/').pop()} ${getPhantomNodeSuffix()}]`;
};
